import { brand, neutral, semantic } from "@/constants/colors";
import { fonts } from "@/constants/fonts";
import { useThemeColor } from "@/hooks/useThemeColor";
import React, { ReactNode } from "react";
import {
  StyleProp,
  StyleSheet,
  TextStyle,
  View,
  ViewStyle,
} from "react-native";
import ThemedText from "./ThemedText";

type BadgeVariant = "filled" | "outlined" | "dot";
type BadgeSize = "sm" | "md" | "lg";
type BadgeColor = "primary" | "success" | "warning" | "error" | "neutral" | "info";
type BadgePosition = "top-right" | "top-left" | "bottom-right" | "bottom-left";

interface BadgeProps {
  content?: string | number;
  variant?: BadgeVariant;
  size?: BadgeSize;
  color?: BadgeColor;
  position?: BadgePosition;
  max?: number;
  showZero?: boolean;
  dot?: boolean;
  children?: ReactNode;
  style?: StyleProp<ViewStyle>;
  badgeStyle?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  invisible?: boolean;
}

export default function Badge({
  content,
  variant = "filled",
  size = "md",
  color = "error",
  position = "top-right",
  max = 99,
  showZero = false,
  dot = false,
  children,
  style,
  badgeStyle,
  textStyle,
  invisible = false,
}: BadgeProps) {
  const isDark = useThemeColor({}, "background") === "#151718";

  const sizeStyles: Record<
    BadgeSize,
    { minWidth: number; height: number; fontSize: number; dotSize: number }
  > = {
    sm: { minWidth: 16, height: 16, fontSize: 10, dotSize: 8 },
    md: { minWidth: 20, height: 20, fontSize: 12, dotSize: 10 },
    lg: { minWidth: 24, height: 24, fontSize: 14, dotSize: 12 },
  };

  const getColors = () => {
    const baseColors = {
      primary: { main: brand.primary.main, faded: brand.primary.faded },
      success: { main: semantic.success.main, faded: semantic.success.faded },
      warning: { main: semantic.warning.main, faded: semantic.warning.faded },
      error: { main: semantic.error.main, faded: semantic.error.faded },
      info: { main: semantic.info.main, faded: semantic.info.faded },
      neutral: { main: isDark ? neutral[600] : neutral[400], faded: isDark ? neutral[800] : neutral[100] },
    };

    return baseColors[color];
  };

  const colors = getColors();

  const getBadgeStyle = (): ViewStyle => {
    const baseStyle: ViewStyle = {
      minWidth: dot ? sizeStyles[size].dotSize : sizeStyles[size].minWidth,
      height: dot ? sizeStyles[size].dotSize : sizeStyles[size].height,
      borderRadius: dot ? sizeStyles[size].dotSize / 2 : sizeStyles[size].height / 2,
      paddingHorizontal: dot ? 0 : 4,
      justifyContent: "center",
      alignItems: "center",
    };

    switch (variant) {
      case "filled":
        return {
          ...baseStyle,
          backgroundColor: colors.main,
        };
      case "outlined":
        return {
          ...baseStyle,
          backgroundColor: "transparent",
          borderWidth: 1.5,
          borderColor: colors.main,
        };
      case "dot":
        return {
          width: sizeStyles[size].dotSize,
          height: sizeStyles[size].dotSize,
          borderRadius: sizeStyles[size].dotSize / 2,
          backgroundColor: colors.main,
        };
    }
  };

  const getTextColor = (): string => {
    switch (variant) {
      case "filled":
        return "#FFFFFF";
      case "outlined":
        return colors.main;
      case "dot":
        return "transparent";
    }
  };

  const getPositionStyle = (): ViewStyle => {
    const offset = -6;
    switch (position) {
      case "top-right":
        return { top: offset, right: offset };
      case "top-left":
        return { top: offset, left: offset };
      case "bottom-right":
        return { bottom: offset, right: offset };
      case "bottom-left":
        return { bottom: offset, left: offset };
    }
  };

  const getDisplayContent = (): string => {
    if (dot || variant === "dot") return "";
    if (content === undefined || content === null) return "";

    const numContent = typeof content === "number" ? content : parseInt(content, 10);

    if (!isNaN(numContent)) {
      if (numContent === 0 && !showZero) return "";
      if (numContent > max) return `${max}+`;
      return numContent.toString();
    }

    return content.toString();
  };

  const displayContent = getDisplayContent();
  const shouldHide = invisible || (displayContent === "" && !dot && variant !== "dot");

  if (!children) {
    // Standalone badge
    if (shouldHide) return null;

    return (
      <View style={[getBadgeStyle(), badgeStyle, style]}>
        {displayContent !== "" && (
          <ThemedText
            style={[
              {
                fontSize: sizeStyles[size].fontSize,
                fontFamily: fonts.bold,
                color: getTextColor(),
              },
              textStyle,
            ]}
          >
            {displayContent}
          </ThemedText>
        )}
      </View>
    );
  }

  // Badge with children (positioned badge)
  return (
    <View style={[styles.container, style]}>
      {children}
      {!shouldHide && (
        <View style={[styles.badge, getPositionStyle(), getBadgeStyle(), badgeStyle]}>
          {displayContent !== "" && (
            <ThemedText
              style={[
                {
                  fontSize: sizeStyles[size].fontSize,
                  fontFamily: fonts.bold,
                  color: getTextColor(),
                },
                textStyle,
              ]}
            >
              {displayContent}
            </ThemedText>
          )}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: "relative",
    display: "flex",
  },
  badge: {
    position: "absolute",
    zIndex: 1,
  },
});
